/**
 * Redux action creators for user authentication, registration, and notifications.
 *
 * @module redux/authentication/action
 * @author Snowfly
 * @version 1.0.0
 */
import axios from 'axios';
import {
    LOGIN_FAIL,
    LOGIN_REQUEST,
    LOGIN_SUCCESS,
    CHECK_AUTHORIZATION,
    USER_LOGOUT,
    SAVE_PERMISSIONS,
    FETCH_FAILURE,
    UPDATE_LOGIN_STATUS,
    REGISTRATION_FAILURE,
    UPDATE_ROLE,
    RESET_NOTIFICATION,
    FETCH_NOTIFICATIONS,
} from './authTypes';
import config from '../../config';

/**
 * Represents an action creator function to dispatch a login request action.
 * This function returns an action object with a specific type indicating
 * the initiation of a login process.
 *
 * @function loginRequest
 * @returns {Object} An action object containing a `type` property with the value `LOGIN_REQUEST`.
 */
export const loginRequest = () => ({
    type: LOGIN_REQUEST,
});

/**
 * Action creator for handling a successful login event.
 *
 * @function
 * @param {Object} payload - The data associated with the successful login action.
 * @returns {Object} Action object containing type `LOGIN_SUCCESS` and the provided payload.
 */
export const loginSuccess = (payload) => ({
    type: LOGIN_SUCCESS,
    payload,
});

/**
 * Action creator function for handling login failure.
 *
 * @param {any} payload - The data associated with the login failure, providing details such as error messages or additional information.
 * @returns {Object} An action object containing the type 'LOGIN_FAIL' and the associated payload.
 */
export const loginFail = (payload) => ({
    type: LOGIN_FAIL,
    payload,
});

// Check Authorization action
/**
 * Represents a function that creates an action for checking authorization.
 * The action returned by this function is used to verify the authorization
 * status in the application.
 *
 * @function checkAuthorization
 * @returns {Object} An action object with a type property of 'CHECK_AUTHORIZATION'.
 */
export const checkAuthorization = () => ({
    type: CHECK_AUTHORIZATION,
});

// User logout action

/**
 * Represents an action creator function for logging out a user.
 *
 * This function returns an action object with the type USER_LOGOUT.
 * It can be dispatched to the Redux store to initiate the user logout process.
 *
 * @function userLogout
 * @returns {Object} Action object with a type property set to USER_LOGOUT.
 */
export const userLogout = () => ({
    type: USER_LOGOUT,
});

// user login actions
/**
 * Handles user login by dispatching the appropriate actions and making a POST request to the login API.
 *
 * @function
 * @param {Object} payload - The login data, typically including user credentials such as username and password.
 * @returns {Function} - A redux-thunk function that takes dispatch as an argument and performs the login process.
 */
export const loginUser = (payload) => (dispatch) => {
    dispatch(loginRequest);
    axios({
        url: `${config.API_BASE_URL}/api/v1/login/`,
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            task: null,
            type: null,
        },
        data: payload,
    }).then((res) => {
        dispatch(loginSuccess(res.data));
    }).catch((err) => {
        dispatch(loginFail(err.response.data));
    });
};

/**
 * Action creator function for handling fetch failure scenarios.
 * Returns an action object with the type `FETCH_FAILURE` and the provided payload data.
 *
 * @function fetchFailure
 * @param {any} data - The data to be passed as the payload in the action object, typically representing error information or failure details.
 * @returns {Object} The action object with properties:
 *                   - `type`: The string constant `FETCH_FAILURE` indicating the action type.
 *                   - `payload`: The `data` parameter passed to the function.
 */
export const fetchFailure = (data) => ({
    type: FETCH_FAILURE,
    payload: data,
});

/**
 * A function that creates an action object to save permission details.
 *
 * @function addPermissionDetails
 * @param {Object} data - The payload containing permission details.
 * @returns {Object} An action object with a type of `SAVE_PERMISSIONS` and the provided payload.
 */
export const addPermissionDetails = (data) => ({
    type: SAVE_PERMISSIONS,
    payload: data,
});

/**
 * Asynchronous function to fetch permission details for a user and dispatch relevant actions.
 *
 * This function performs the following tasks:
 * 1. Dispatches an action to indicate the login request has started.
 * 2. Makes an HTTP GET request to fetch user permission details from the API.
 * 3. Dispatches an action to add the fetched permission details to the state upon success.
 * 4. Dispatches a failure action in case of an error during the API call.
 *
 * API Endpoint: `${config.API_BASE_URL}/api/v1/permissions/user-details`
 *
 * Headers:
 * - Authorization: Token retrieved from the configuration (`config.getToken()`).
 * - task: null (reserved for additional task metadata, if needed).
 * - type: null (reserved for additional type metadata, if needed).
 *
 * Actions:
 * - `loginRequest`: Dispatched before initiating the API call.
 * - `addPermissionDetails(res.data)`: Dispatched with API response data upon successful call.
 * - `fetchFailure(error)`: Dispatched with error details upon API call failure.
 *
 * Note: Ensure that `config.API_BASE_URL` and `config.getToken()` are correctly configured before invoking this function.
 */
export const fetchPermissionDetails = () => async (dispatch) => {
    dispatch(loginRequest);
    await axios.get(`${config.API_BASE_URL}/api/v1/permissions/user-details`, {
        headers: {
            Authorization: config.getToken(),
            task: null,
            type: null,
        },
    })
        .then((res) => dispatch(addPermissionDetails(res.data)))
        .catch((error) => dispatch(fetchFailure(error)));
};

/**
 * Action creator for updating the login status in the application state.
 *
 * @function
 * @param {Object} payload - The data object containing information related to the updated login status.
 * @returns {Object} Action object containing the type of action and the payload.
 */
export const updateLoginStatus = (payload) => ({
    type: UPDATE_LOGIN_STATUS,
    payload,
});

/**
 * Represents an action creator for handling registration failure.
 * This function returns an action object with a specific type and
 * payload containing the error or data associated with the failure.
 *
 * @param {Object} data - The data or error information related to the registration failure.
 * @returns {Object} An action object containing the type `REGISTRATION_FAILURE`
 *                   and the associated payload.
 */
export const registrationFailed = (data) => ({
    type: REGISTRATION_FAILURE,
    payload: data,
});

/**
 * Performs a user registration request to the API and dispatches the appropriate actions based on the response.
 *
 * @param {Object} data - The user registration data payload to be sent in the request.
 * @returns {Function} A thunk function to handle the asynchronous registration process.
 *
 * Dispatches the following actions:
 * - `loginRequest` before making the API call.
 * - `registrationFailed` with the response data on success or error.
 *
 * Uses axios for HTTP communication and handles errors by dispatching the appropriate failure action.
 */
export const registerUser = (data) => (dispatch) => {
    dispatch(loginRequest);
    axios({
        url: `${config.API_BASE_URL}/api/v1/users/register`,
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            task: null,
            type: null,
        },
        data,
    }).then((res) => {
        dispatch(registrationFailed(res.data));
    }).catch((err) => {
        dispatch(registrationFailed(err.response.data));
    });
};

/**
 * Action creator function for updating the role in the application state.
 *
 * @param {Object} payload - The data containing the updated role information.
 * @returns {Object} An action object with the type 'UPDATE_ROLE' and the provided payload.
 */
export const updateRole = (payload) => ({
    type: UPDATE_ROLE,
    payload,
});

/**
 * Asynchronous action creator for logging in to an agent account.
 *
 * This function dispatches a login request action, makes an HTTP POST request
 * to the agent account login endpoint, and then dispatches either a success or
 * failure action based on the API's response.
 *
 * @function
 * @param {Object} payload - The data payload containing login credentials.
 * @returns {Function} A thunk function that accepts `dispatch` as an argument
 * and performs the login API call.
 */
export const loginToAgentAccount = (payload) => (dispatch) => {
    dispatch(loginRequest);
    axios({
        url: `${config.API_BASE_URL}/api/v1/login/login-to-agent-account`,
        method: 'POST',
        headers: {
            task: null,
            type: null,
        },
        data: payload,
    }).then((res) => {
        dispatch(loginSuccess(res.data));
    }).catch((err) => {
        dispatch(loginFail(err.response.data));
    });
};

/**
 * Action creator function for resetting notification state in the application.
 * This function returns an action object with a `type` property set to `RESET_NOTIFICATION`.
 * Typically used to reset any notification-related state values in the Redux store.
 *
 * @returns {Object} An action object with a `type` property.
 */
export const resetNotification = () => ({
    type: RESET_NOTIFICATION,
});

/**
 * An action creator function that constructs and returns an action
 * object to fetch the notification list. This action is typically
 * dispatched to update the state in the application regarding
 * notifications.
 *
 * @param {Object} data - The data payload that contains notification-related
 *                        information to be passed in the action.
 * @returns {Object} An action object with a type of FETCH_NOTIFICATIONS
 *                   and the provided data as the payload.
 */
export const fetchNotifyList = (data) => ({
    type: FETCH_NOTIFICATIONS,
    payload: data,
});

/**
 * Fetches a paginated list of notifications from the server and dispatches relevant actions
 * to update the application state based on the result of the request.
 *
 * @function fetchNotification
 * @param {number} pageNumber - The current page number used to calculate the offset for pagination.
 * @param {number} notificationListingPage - Identifier for the specific notification listing page.
 * @returns {Function} A Redux thunk that performs an asynchronous operation and dispatches actions.
 */
export const fetchNotification = (pageNumber, notificationListingPage) => async (dispatch) => {
    dispatch(loginRequest);
    const limit = 10;
    const skip = limit * pageNumber;
    await axios(`${config.API_BASE_URL}/api/v1/users/notification-listing?skip=${skip}&limit=${limit}&notificationListingPage=${notificationListingPage}`,
        {
            headers: {
                Authorization: config.getToken(),
                task: null,
                type: null,
            },
        })
        .then((res) => dispatch(fetchNotifyList(res)))
        .catch((error) => dispatch(fetchFailure(error)));
};

/**
 * Resets the specified notifications to a read state.
 *
 * The `resetNotifications` function takes an array of notification IDs and
 * triggers an asynchronous call to mark those notifications as read. The function
 * dispatches a Redux action before and after performing this operation to update
 * the application state.
 *
 * @param {Array} notificationIds - An array of notification IDs to be marked as read.
 * @returns {Function} A thunk function that performs the asynchronous operation and dispatches actions.
 */
export const resetNotifications = (notificationIds) => async (dispatch) => {
    dispatch(resetNotification())
    await axios.post(`${config.API_BASE_URL}/api/v1/users/notification-read`,
        {notificationIds},
        {
            headers: {
                Authorization: config.getToken(),
                task: null,
                type: null,
            },
        })
        .then((/* res */) => dispatch(resetNotification()))
        .catch((error) => dispatch(fetchFailure(error)));
};
